/**
 * DocumentList Component
 * 
 * Table/list layout for displaying documents
 */

import React from 'react';
import { Document, DocumentType, DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_COLORS } from '../types/documentsTypes';
import { useDocumentsStore } from '../stores/documentsStore';
import { 
  FileText, Download, Eye, Pencil, Trash2, Share2, MoreHorizontal,
  FileX, CheckSquare, Square, ChevronUp, ChevronDown
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { formatDistanceToNow } from 'date-fns';

// ============================================================================
// Types
// ============================================================================

export interface DocumentListProps {
  documents: Document[];
  isLoading?: boolean;
  onDocumentClick?: (document: Document) => void;
  onDocumentAction?: (action: string, document: Document) => void;
  onSort?: (field: string) => void;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  className?: string;
  emptyMessage?: string;
}

interface ColumnConfig {
  key: string;
  label: string;
  sortable: boolean;
  width?: string;
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const COLUMNS: ColumnConfig[] = [
  { key: 'select', label: '', sortable: false, width: 'w-10' },
  { key: 'title', label: 'Name', sortable: true, className: 'flex-1 min-w-0' },
  { key: 'document_type', label: 'Type', sortable: true, width: 'w-32' },
  { key: 'file_size_bytes', label: 'Size', sortable: true, width: 'w-24' },
  { key: 'updated_at', label: 'Modified', sortable: true, width: 'w-32' },
  { key: 'actions', label: '', sortable: false, width: 'w-20' },
];

// ============================================================================
// Helpers
// ============================================================================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// ============================================================================
// Subcomponents
// ============================================================================

interface DocumentRowProps {
  document: Document;
  isSelected: boolean;
  onSelect: (id: string, selected: boolean) => void;
  onClick?: () => void;
  onAction?: (action: string) => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({
  document,
  isSelected,
  onSelect,
  onClick,
  onAction,
}) => {
  const [showActions, setShowActions] = React.useState(false);

  return (
    <tr 
      className={cn(
        'group border-b border-gray-100 hover:bg-cream/50 transition-colors cursor-pointer',
        isSelected && 'bg-sage/5'
      )}
      onClick={onClick}
    >
      {/* Checkbox */}
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onSelect(document.id, !isSelected)}
          className="p-1 rounded hover:bg-cream"
        >
          {isSelected ? (
            <CheckSquare className="w-4 h-4 text-sage" />
          ) : (
            <Square className="w-4 h-4 text-warm-gray" />
          )}
        </button>
      </td>

      {/* Name */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded bg-cream flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-warm-gray" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-navy truncate">{document.title}</p>
            {document.provider_name && (
              <p className="text-xs text-warm-gray truncate">{document.provider_name}</p>
            )}
          </div>
          {document.is_shared_with_attorney && (
            <Share2 className="w-4 h-4 text-sage flex-shrink-0" />
          )}
        </div>
      </td>

      {/* Type */}
      <td className="px-3 py-3">
        <span 
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
            DOCUMENT_TYPE_COLORS[document.document_type]
          )}
        >
          {DOCUMENT_TYPE_LABELS[document.document_type]}
        </span>
      </td>

      {/* Size */}
      <td className="px-3 py-3 text-sm text-warm-gray">
        {formatFileSize(document.file_size_bytes)}
      </td>

      {/* Modified */}
      <td className="px-3 py-3 text-sm text-warm-gray">
        {formatDistanceToNow(new Date(document.updated_at), { addSuffix: true })}
      </td>

      {/* Actions */}
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1 rounded hover:bg-cream opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreHorizontal className="w-4 h-4 text-warm-gray" />
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowActions(false)} 
              />
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 min-w-[140px]">
                <button
                  onClick={() => { onAction?.('view'); setShowActions(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-cream"
                >
                  <Eye className="w-4 h-4" /> View
                </button>
                <button
                  onClick={() => { onAction?.('download'); setShowActions(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-cream"
                >
                  <Download className="w-4 h-4" /> Download
                </button>
                <button
                  onClick={() => { onAction?.('edit'); setShowActions(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-cream"
                >
                  <Pencil className="w-4 h-4" /> Edit
                </button>
                <hr className="my-1 border-gray-100" />
                <button
                  onClick={() => { onAction?.('delete'); setShowActions(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const SkeletonRow: React.FC = () => (
  <tr className="border-b border-gray-100">
    <td className="px-3 py-3"><div className="w-4 h-4 bg-gray-200 rounded animate-pulse" /></td>
    <td className="px-3 py-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-48 animate-pulse" />
          <div className="h-3 bg-gray-200 rounded w-32 mt-1 animate-pulse" />
        </div>
      </div>
    </td>
    <td className="px-3 py-3"><div className="h-5 bg-gray-200 rounded w-20 animate-pulse" /></td>
    <td className="px-3 py-3"><div className="h-4 bg-gray-200 rounded w-16 animate-pulse" /></td>
    <td className="px-3 py-3"><div className="h-4 bg-gray-200 rounded w-24 animate-pulse" /></td>
    <td className="px-3 py-3"><div className="w-6 h-6 bg-gray-200 rounded animate-pulse" /></td>
  </tr>
);

// ============================================================================
// Main Component
// ============================================================================

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  isLoading = false,
  onDocumentClick,
  onDocumentAction,
  onSort,
  sortField,
  sortOrder,
  className,
  emptyMessage = 'No documents found',
}) => {
  const { selectedDocumentIds, toggleDocumentSelection, selectAllDocuments, clearSelection } = useDocumentsStore();

  const allSelected = documents.length > 0 && documents.every(d => selectedDocumentIds.includes(d.id));
  const someSelected = documents.some(d => selectedDocumentIds.includes(d.id));

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAllDocuments(documents.map(d => d.id));
    }
  };

  const handleSort = (field: string) => {
    onSort?.(field);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('overflow-x-auto', className)}>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              {COLUMNS.map((col) => (
                <th key={col.key} className={cn('px-3 py-3 text-xs font-medium text-warm-gray uppercase', col.width)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Empty state
  if (documents.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-16 px-4', className)}>
        <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mb-4">
          <FileX className="w-8 h-8 text-warm-gray" />
        </div>
        <h3 className="text-lg font-medium text-navy mb-2">{emptyMessage}</h3>
      </div>
    );
  }

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            {/* Select All */}
            <th className="px-3 py-3 w-10">
              <button onClick={handleSelectAll} className="p-1 rounded hover:bg-cream">
                {allSelected ? (
                  <CheckSquare className="w-4 h-4 text-sage" />
                ) : someSelected ? (
                  <div className="w-4 h-4 border-2 border-sage bg-sage/20 rounded" />
                ) : (
                  <Square className="w-4 h-4 text-warm-gray" />
                )}
              </button>
            </th>

            {COLUMNS.slice(1).map((col) => (
              <th 
                key={col.key} 
                className={cn(
                  'px-3 py-3 text-xs font-medium text-warm-gray uppercase',
                  col.width,
                  col.className,
                  col.sortable && 'cursor-pointer hover:text-navy'
                )}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortField === col.key && (
                    sortOrder === 'asc' ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              isSelected={selectedDocumentIds.includes(document.id)}
              onSelect={toggleDocumentSelection}
              onClick={() => onDocumentClick?.(document)}
              onAction={(action) => onDocumentAction?.(action, document)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DocumentList;


